import axios from 'axios';
import { downloadBlob } from './invoicePdf';

// Standard report endpoints (gig.api app/reports): every catalog report is served the same way.
const BASE_URL = '/v1/reports';

let catalogRequest = null;

/** Reports the signed-in user may open, grouped for the menu, with their filters. Cached per page load. */
export function getReportCatalog({ refresh = false } = {}) {
    if (!catalogRequest || refresh) {
        catalogRequest = axios.get(`${BASE_URL}/catalog`)
            .then((response) => response.data?.groups || [])
            .catch((error) => {
                catalogRequest = null;
                throw error;
            });
    }
    return catalogRequest;
}

export async function findReportDefinition(key) {
    const groups = await getReportCatalog();
    for (const group of groups) {
        const report = group.reports.find((item) => item.key === key);
        if (report) return report;
    }
    return null;
}

/** Drop empty values so the server applies its own defaults. */
function cleanParams(params) {
    return Object.fromEntries(Object.entries(params || {}).filter(([, value]) => value !== null && value !== undefined && value !== ''));
}

export async function getReport(key, params) {
    const response = await axios.get(`${BASE_URL}/${encodeURIComponent(key)}`, { params: cleanParams(params) });
    return response.data;
}

/** Download the report as an Excel or PDF file in the standard layout. */
export async function downloadReport(key, params, format = 'excel') {
    const response = await axios.get(`${BASE_URL}/${encodeURIComponent(key)}/export`, {
        params: { ...cleanParams(params), format },
        responseType: 'blob',
    });
    downloadBlob(response.data, fileNameFrom(response, `${key}.${format === 'pdf' ? 'pdf' : 'xlsx'}`));
}

/** Download a file from any report export URL (used by the older report pages). */
export async function downloadExport(url, params, fallbackName) {
    const response = await axios.get(url, { params: cleanParams(params), responseType: 'blob' });
    downloadBlob(response.data, fileNameFrom(response, fallbackName));
}

function fileNameFrom(response, fallbackName) {
    const header = response.headers?.['content-disposition'] || '';
    const match = header.match(/filename="?([^";]+)"?/i);
    return match ? match[1] : fallbackName;
}

/** Error responses of blob requests arrive as a Blob; read the server's message out of it. */
export async function errorMessage(error) {
    const data = error?.response?.data;
    if (data instanceof Blob) {
        try {
            const parsed = JSON.parse(await data.text());
            return parsed.detail || parsed.message || error.message;
        } catch {
            return error.message;
        }
    }
    const detail = data?.detail;
    if (Array.isArray(detail)) return detail.map((item) => item.msg).join(', ');
    return detail || data?.message || error?.message || 'Something went wrong';
}
